import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';
import { Pause, Play } from 'lucide-react-native';
import { useEffect } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Caption } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';

interface VoicePreviewButtonProps {
  previewUrl: string | undefined | null;
  testID?: string;
  /** Id used to coordinate single-at-a-time playback across a voice catalog. */
  voiceId?: string;
  /** The voiceId currently allowed to play; when it points elsewhere this button pauses. */
  activeVoiceId?: string | null;
  /** Called with this button's `voiceId` when the user starts playback. */
  onPlayStart?: (voiceId: string) => void;
}

/**
 * Minimal play/pause control that plays a voice sample from `previewUrl`.
 * Renders nothing (null) when previewUrl is absent — callers only mount this
 * when a voice with a previewUrl is selected, so the disabled state is handled
 * at the parent level.
 *
 * Audio: uses expo-audio's `useAudioPlayer` — the same API as `AudioPlayer.tsx`.
 * Works on iOS, Android, and Web.
 */
export function VoicePreviewButton({
  previewUrl,
  testID,
  voiceId,
  activeVoiceId,
  onPlayStart,
}: VoicePreviewButtonProps) {
  const { t } = useTranslation();
  const url = previewUrl ?? '';
  const player = useAudioPlayer(url ? { uri: url } : null);
  const status = useAudioPlayerStatus(player);

  const isPlaying = status.playing;

  // Stop playback and release resources when unmounted or when the URL changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional cleanup on url change
  useEffect(() => {
    return () => {
      player.pause();
    };
  }, [url]);

  // Catalog coordination: when another voice becomes the active one, pause this
  // player so at most one sample plays at a time.
  // biome-ignore lint/correctness/useExhaustiveDependencies: player identity is stable per url
  useEffect(() => {
    if (voiceId && activeVoiceId !== undefined && activeVoiceId !== voiceId) {
      player.pause();
    }
  }, [activeVoiceId, voiceId]);

  if (!url) {
    return (
      <View style={styles.disabledRow} testID={testID ? `${testID}-disabled` : undefined}>
        <View style={styles.disabledButton}>
          <Play size={16} color={colors.text.tertiary} />
        </View>
        <Caption style={styles.disabledLabel}>
          {t('voiceAgents.wizard.voice.previewUnavailable')}
        </Caption>
      </View>
    );
  }

  const handlePress = () => {
    if (isPlaying) {
      player.pause();
    } else {
      if (voiceId) {
        onPlayStart?.(voiceId);
      }
      player.seekTo(0).catch(() => {
        // ignore seek errors — still attempt play
      });
      player.play();
    }
  };

  return (
    <View style={styles.row}>
      <Pressable
        onPress={handlePress}
        style={({ pressed }) => [styles.button, pressed && styles.pressed]}
        accessibilityRole="button"
        accessibilityLabel={
          isPlaying
            ? t('voiceAgents.wizard.voice.previewPause')
            : t('voiceAgents.wizard.voice.previewPlay')
        }
        accessibilityState={{ busy: !status.isLoaded }}
        hitSlop={8}
        testID={testID}
      >
        {isPlaying ? (
          <Pause size={16} color={colors.background.primary} />
        ) : (
          <Play size={16} color={colors.background.primary} />
        )}
      </Pressable>
      <Caption style={styles.label}>
        {isPlaying
          ? t('voiceAgents.wizard.voice.previewPlaying')
          : t('voiceAgents.wizard.voice.previewVoice')}
      </Caption>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  button: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary.main,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.75,
  },
  label: {
    color: colors.text.secondary,
  },
  disabledRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  disabledButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.border.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledLabel: {
    color: colors.text.tertiary,
  },
});
